import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  buildScopedAdminQueryRoot,
  type AdminQueryKey,
  type OrganizationId,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { versionConflictCurrent } from '@/lib/version-conflict'
import {
  getOrganizationTaxSettings,
  getTaxRateSuggestion,
  updateOrganizationTaxSettings,
  type TaxRateSuggestionQueryKey,
} from './organization-tax-settings.api'
import type { OrganizationTaxSettingsInput } from './organization-tax-settings.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

export const organizationTaxSettingsQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'organization-tax-settings' }, { organizationId }),
  ],
  detail: ({ organizationId }: OrganizationScope = {}) =>
    buildScopedAdminQueryKey(organizationTaxSettingsQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'detail',
    }),
}

export const organizationTaxSettingsQueries = {
  detail: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: organizationTaxSettingsQueryKeys.detail({ organizationId }),
      queryFn: getOrganizationTaxSettings,
    }),
}

export function useOrganizationTaxSettings({ organizationId }: OrganizationScope = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery(
    organizationTaxSettingsQueries.detail({ organizationId: resolvedOrganizationId })
  )
}

export function useUpdateOrganizationTaxSettingsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: OrganizationTaxSettingsInput) =>
      updateOrganizationTaxSettings({ organizationId, ...values }),
    onError: (error) => {
      const current = versionConflictCurrent(error)
      if (current) {
        queryClient.setQueryData(
          organizationTaxSettingsQueryKeys.detail({ organizationId }),
          current
        )
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: organizationTaxSettingsQueryKeys.detail({ organizationId }),
      }),
  })
}

export function useTaxRateSuggestion({
  organizationId,
  locationId,
}: {
  organizationId: OrganizationId
  locationId: number | undefined
}) {
  const queryKey: TaxRateSuggestionQueryKey = [
    buildScopedAdminQueryRoot(organizationTaxSettingsQueryKeys.all({ organizationId })[0], {
      organizationId,
    }),
    { scope: 'rate-suggestion', locationId },
  ]

  return useQuery({
    queryKey,
    queryFn: getTaxRateSuggestion,
    enabled: false,
    retry: false,
  })
}
